/** @jsxImportSource @emotion/react */
import { MoreOutlined } from "@ant-design/icons";
import { css } from "@emotion/react";
import { UserEdition } from "@encoo-web/encoo-lib/types";
import { Button, Dropdown, Input, Menu, Table, Tooltip } from "antd";
import { TablePaginationConfig } from "antd/lib/table";
import _ from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { EnterpriseAccount, EnterpriseDetailList } from "src/models/company";
import { AccountSeviceClient } from "src/services/CompanyService";
import CommunityDetail from "./components/CommunityDetail";

const { Search } = Input;

export default React.memo(function CommunityContext() {
  const formatMessage = useFormatMessage();
  const [isShowDetail, setIsShowDetail] = useState(false);
  const [showDetail, setShowDetail] = useState({} as EnterpriseDetailList);
  const [nameChange, setNameChange] = useState("");
  const [dataSource, setDataSource] = useState([] as EnterpriseAccount[]);
  const [pagination, setPagiation] = useState({
    current: 1,
    pageSize: 10,
  } as TablePaginationConfig);
  const [maxPage, setMaxPage] = useState(0);
  const [lastRowKey, setLastRowKey] = useState("");
  const [limit] = useState(1000);
  const [noMoreData, setNoMoreData] = useState(false);
  const [edition] = useState(1 as UserEdition);
  const [loading, setLoading] = useState(true);

  const onClose = useCallback(() => {
    setIsShowDetail(false);
  }, []);

  const onView = useCallback((record: EnterpriseAccount) => {
    setIsShowDetail(true);
    AccountSeviceClient.getDetail(record.id)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((res: any) => {
        setShowDetail(res.data);
      });
  }, []);

  const menu = useCallback(
    (record: EnterpriseAccount) => (
      <Menu>
        <Menu.Item
          key="view"
          css={css`
            font-size: 12px;
            :hover {
              color: rgb(51, 119, 255);
              border-radius: 4px;
              cursor: pointer;
              background: rgb(51, 119, 255, 0.1);
            }
          `}
          onClick={() => onView(record)}
        >
          {formatMessage(messageIds.common.view)}
        </Menu.Item>
      </Menu>
    ),
    [formatMessage, onView]
  );

  const columns = useMemo(
    () => [
      {
        title: formatMessage(messageIds.company.companyId),
        dataIndex: "id",
      },
      {
        title: formatMessage(messageIds.company.companyName),
        dataIndex: "name",
        width: "25%",
        render: (text: string, record: EnterpriseAccount) => (
          <Button
            type="link"
            onClick={() => onView(record)}
            style={{ padding: 0 }}
          >
            {text}
          </Button>
        ),
      },
      {
        title: formatMessage(messageIds.company.companyType),
        dataIndex: "edition",
        width: "20%",
        render: (text: string) =>
          text === "Enterprise"
            ? formatMessage(messageIds.edition.enterprise)
            : formatMessage(messageIds.edition.community),
      },
      {
        title: formatMessage(messageIds.common.creationTime),
        dataIndex: "createdAt",
        render: (text: string, record: EnterpriseAccount) =>
          moment(text).format("YYYY-MM-DD HH:mm:ss"),
      },
      {
        title: formatMessage(messageIds.common.operation),
        dataIndex: "operation",
        render: (text: string, record: EnterpriseAccount) => (
          <Dropdown
            overlay={menu(record)}
            placement="bottomLeft"
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              width: 30px;
              height: 30px;
              :hover {
                color: rgb(51, 119, 255);
                border-radius: 4px;
                cursor: pointer;
                background: rgb(51, 119, 255, 0.1);
              }
            `}
          >
            <MoreOutlined />
          </Dropdown>
        ),
      },
    ],
    [formatMessage, onView, menu]
  );

  const onNameChange = useCallback((e) => {
    setNameChange(e.target.value);
  }, []);

  let isUnmounted = false;
  const fetch = useCallback(
    (
      Name?: string,
      Edition?: UserEdition,
      Limit?: number,
      LastRowKey?: string
    ) => {
      if ((pagination.current as number) >= (maxPage as number)) {
        if (!noMoreData) {
          AccountSeviceClient.list(
            _.trim(Name),
            Edition,
            Limit,
            LastRowKey
          ).then((res) => {
            if (!isUnmounted) {
              setDataSource(dataSource.concat(res.data.data));
              setLastRowKey(res.data.nextPage.rowKey);
              setNoMoreData(res.data.data.length < 1000);
              setLoading(false);
            }
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formatMessage, pagination.current]
  );

  const onTableChange = useCallback(
    (pagination: TablePaginationConfig) => {
      setPagiation(pagination);
      const currentMax = Math.floor(dataSource.length / 10);
      setMaxPage(currentMax);
    },
    [dataSource.length]
  );

  const onSearch = useCallback(async () => {
    setLoading(true);
    setNoMoreData(false);
    if (_.trim(nameChange) !== "") {
      setDataSource([]);
      setLastRowKey("");
      setMaxPage(0);
      setPagiation({ current: 1, pageSize: 10 });
      fetch(nameChange, edition, limit, "");
    } else {
      setDataSource([]);
      setLastRowKey("");
      setMaxPage(0);
      setPagiation({ current: 1, pageSize: 10 });
      fetch(nameChange, edition, limit, "");
    }
  }, [edition, fetch, limit, nameChange]);

  useEffect(() => {
    fetch(nameChange, edition, limit, lastRowKey);
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      isUnmounted = true;
    };
  }, [pagination?.current, limit, edition]);

  return (
    <div
      css={css`
        flex-grow: 1;
        padding: 30px;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-right: 30px;
        `}
      >
        <div
          css={css`
            max-width: 1000px;
            margin-right: 20px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `}
        >
          <h1
            css={css`
              position: relative;
              margin: 0px 0px 8px;
              padding-left: 10px;
              line-height: 18px;
              font-size: 18px;
              font-weight: 600;
              color: rgb(61, 73, 102);
              ::before {
                content: "";
                position: absolute;
                left: 0px;
                width: 3px;
                height: 18px;
                background: rgb(51, 119, 255);
              }
            `}
          >
            {formatMessage(messageIds.menu.company.community.name)}
          </h1>
          <Tooltip
            title={formatMessage(
              messageIds.company.introduce.community.description
            )}
          >
            <span
              css={css`
                margin-left: 10px;
                color: rgb(143, 155, 179);
              `}
            >
              {formatMessage(
                messageIds.company.introduce.community.description
              )}
            </span>
          </Tooltip>
        </div>
      </div>
      <div
        css={css`
          display: flex;
          margin-top: 20px;
          padding-bottom: 24px;
          border-bottom: 1px solid rgb(202, 209, 224);
        `}
      >
        <Search
          css={css`
            width: 300px;
          `}
          placeholder={formatMessage(messageIds.company.companyName)}
          onChange={onNameChange}
          onSearch={onSearch}
          enterButton
        ></Search>
      </div>
      <div
        css={css`
          margin-top: 30px;
        `}
      >
        <Table
          // css={css`
          //   .ant-table-thead > tr > th {
          //     background: white;
          //   }
          // `}
          columns={columns}
          dataSource={dataSource}
          rowKey={(record) => record?.id}
          onChange={(pagination) => onTableChange(pagination)}
          pagination={{ ...pagination, showSizeChanger: false }}
          loading={loading}
        ></Table>
      </div>
      <CommunityDetail
        isShowDetail={isShowDetail}
        showDetail={showDetail}
        onClose={onClose}
        fetch={onSearch}
      ></CommunityDetail>
    </div>
  );
});
